import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { X, Search, Bot, User, Users, Send, Maximize2, Minimize2, Paperclip, FileText, Image as ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { theme } from '../styles/theme';
import { Spinner } from './ui';
import api from '../services/api';

const MAX_FILE_SIZE = 16 * 1024 * 1024; // 16 MB

/**
 * NewChatDrawer
 * Drawer lateral/bottom-sheet para iniciar nova conversa interna:
 *   - Agentes IA, Membros, Grupos
 * Ao selecionar, abre o chat inline no próprio drawer.
 */
const NewChatDrawer = ({ tenantId, currentUser, onClose, onSelectChat }) => {
  const { t } = useTranslation('callcenter');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(false);
  const [agents, setAgents] = useState([]);
  const [members, setMembers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);

  // Chat inline
  const [active, setActive] = useState(null); // { type, id, name, connectorId? }
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [attachedFile, setAttachedFile] = useState(null);
  const [mediaUrls, setMediaUrls] = useState({}); // { s3Key: presignedUrl }
  const fileInputRef = useRef(null);
  const lastPollRef = useRef(null);
  const pollRef = useRef(null);
  const endRef = useRef(null);
  const [unreadFrom, setUnreadFrom] = useState(null); // senderId com mensagem não lida

  useEffect(() => {
    // Só carrega se ainda não tem dados (evita flickering em re-renders)
    if (agents.length || members.length || groups.length) return;
    setLoading(true);
    Promise.all([
      api.get(`/connectors/${tenantId}`),
      api.get(`/marketplace/tenants/${tenantId}/agents`),
      api.get(`/teams/${tenantId}/members`),
      api.get(`/wa-groups/${tenantId}/team-groups`),
    ]).then(([connRes, agRes, memRes, tgRes]) => {
      const internalConns = (connRes.data.connectors || []).filter(c => c.type === 'internal_chat' && c.enabled);
      const installed = agRes.data.agents || [];
      setAgents(internalConns.map(c => {
        const ag = installed.find(a => a.instanceId === c.agentInstanceId || a.agentId === c.agentInstanceId);
        return { type: 'agent', id: c.agentInstanceId, connectorId: c.connectorId, name: ag?.instanceName || ag?.name || t('aiAgentFallback', { defaultValue: 'Agente IA' }) };
      }));
      const me = currentUser?.email || currentUser?.sub;
      setMembers((memRes.data.members || []).filter(m => m.email !== me && m.memberId !== me));
      setGroups(tgRes.data.teamGroups || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, [tenantId]); // eslint-disable-line

  // Poll global para notificação de mensagens novas (mesmo sem conversa aberta)
  const globalPollRef = useRef(null);
  const globalLastPollRef = useRef(null);

  useEffect(() => {
    // Inicializa com timestamp atual para só detectar mensagens NOVAS (não existentes)
    if (!globalLastPollRef.current) globalLastPollRef.current = new Date().toISOString();

    const globalPoll = async () => {
      if (active) return; // se já tem conversa aberta, o poll específico cuida
      try {
        const params = { since: globalLastPollRef.current };
        const res = await api.get(`/internal-chat/${tenantId}/poll`, { params });
        const { messages: newMsgs, timestamp } = res.data;
        if (newMsgs?.length) {
          // Notifica que há mensagem nova de alguém
          const senderNormalized = (newMsgs[0].senderId || '').replace(/[^a-zA-Z0-9]/g, '_');
          setUnreadFrom(senderNormalized || newMsgs[0].senderId);
          const lastTs = newMsgs[newMsgs.length - 1].createdAt;
          globalLastPollRef.current = new Date(new Date(lastTs).getTime() + 1).toISOString();
        }
      } catch { }
    };
    globalPollRef.current = setInterval(globalPoll, 5000);
    return () => clearInterval(globalPollRef.current);
  }, [tenantId, active]); // eslint-disable-line

  // Limpa notificação quando abre conversa com o remetente
  useEffect(() => {
    if (active && unreadFrom === active.id) setUnreadFrom(null);
  }, [active, unreadFrom]);

  // Poll para respostas da conversa ativa
  useEffect(() => {
    if (!active) return;
    setMessages([]);
    lastPollRef.current = null;

    // Carrega histórico completo do thread ao abrir conversa
    const loadHistory = async () => {
      if (active.type !== 'human') return;
      try {
        const threadId = [currentUser?.email?.replace(/[^a-zA-Z0-9]/g, '_') || '', active.id].sort().join('_');
        const res = await api.get(`/internal-chat/${tenantId}/threads`, { params: { threadId } });
        const threadMsgs = res.data.messages || [];
        if (threadMsgs.length) {
          const myId = currentUser?.email || currentUser?.sub || '';
          const myMemberId = myId.replace(/[^a-zA-Z0-9]/g, '_');
          setMessages(threadMsgs.map(m => {
            const senderNorm = (m.senderId || '').replace(/[^a-zA-Z0-9]/g, '_');
            const isMe = m.senderId === myId || senderNorm === myMemberId;
            return { from: isMe ? 'me' : 'other', sender: m.senderName || m.senderId, text: m.message, attachments: m.attachments, time: fmtTime(m.createdAt) };
          }));
          // Seta lastPoll para depois da última mensagem do histórico
          const lastTs = threadMsgs[threadMsgs.length - 1].createdAt;
          lastPollRef.current = new Date(new Date(lastTs).getTime() + 1).toISOString();
        }
      } catch { }
    };
    loadHistory();

    pollRef.current = setInterval(doPoll, 3000);
    return () => clearInterval(pollRef.current);
  }, [active]); // eslint-disable-line

  const doPoll = async () => {
    if (!active) return;
    try {
      const params = lastPollRef.current ? { since: lastPollRef.current } : {};
      const res = await api.get(`/internal-chat/${tenantId}/poll`, { params });
      const { messages: newMsgs, agentReply, timestamp } = res.data;

      if (newMsgs?.length) {
        const relevant = newMsgs.filter(m => {
          if (active.type === 'group') return m.groupId === active.id || m.threadId === `group:${active.id}`;
          if (active.type === 'human') {
            const senderNormalized = (m.senderId || '').replace(/[^a-zA-Z0-9]/g, '_');
            return senderNormalized === active.id || m.senderId === active.id || m.threadId?.includes(active.id);
          }
          return false;
        });
        if (relevant.length) {
          setMessages(prev => {
            // Deduplica por texto+time para evitar repetição
            const existing = new Set(prev.map(m => `${m.text}|${m.time}`));
            const newOnes = relevant
              .map(m => ({ from: 'other', sender: m.senderName || m.senderId, text: m.message, attachments: m.attachments, time: fmtTime(m.createdAt) }))
              .filter(m => !existing.has(`${m.text}|${m.time}`));
            return newOnes.length ? [...prev, ...newOnes] : prev;
          });
          // Avança lastPoll 1ms além da última mensagem para evitar re-fetch
          const lastTs = relevant[relevant.length - 1].createdAt;
          const advanced = new Date(new Date(lastTs).getTime() + 1).toISOString();
          lastPollRef.current = advanced;
        }
      }
      if (active.type === 'agent' && agentReply) {
        setMessages(prev => [...prev, { from: 'other', sender: active.name, text: agentReply.message, attachments: agentReply.attachments, time: fmtTime(agentReply.createdAt) }]);
        lastPollRef.current = agentReply.createdAt;
      }
      // Seta timestamp inicial para que próximos polls usem since
      if (!lastPollRef.current) lastPollRef.current = timestamp || new Date().toISOString();
    } catch { }
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE) {
      alert(t('fileTooLarge', { ns: 'callcenter', defaultValue: 'Arquivo muito grande. Máximo: 16 MB.' }));
      e.target.value = '';
      return;
    }
    setAttachedFile(file);
    e.target.value = '';
  };

  const removeAttachedFile = () => setAttachedFile(null);

  // Busca (uma vez) a URL presigned de download para um s3Key e cacheia.
  const loadMediaUrl = async (s3Key) => {
    if (!s3Key || mediaUrls[s3Key]) return;
    try {
      const res = await api.get(`/media/presigned?key=${encodeURIComponent(s3Key)}`);
      setMediaUrls(prev => ({ ...prev, [s3Key]: res.data.url }));
    } catch (e) { /* silencioso — placeholder permanece */ }
  };

  const send = async () => {
    if (sending) return;
    const text = input.trim();
    if (!text && !attachedFile) return;

    // Grupos (wa_group) ainda não suportam anexo pelo chat interno
    if (attachedFile && active.type === 'group') {
      alert(t('attachmentNotSupportedGroup', { ns: 'callcenter', defaultValue: 'Anexos não são suportados em grupos.' }));
      return;
    }

    setSending(true);
    const fileToSend = attachedFile;
    setInput('');
    setAttachedFile(null);

    try {
      let attachmentMeta = null;

      // 1. Faz upload do arquivo (se houver) e obtém s3Key
      if (fileToSend) {
        const sessionId = `internal_${active.type}_${active.id}`;
        const { data } = await api.post(`/conversations/${tenantId}/upload-media`, {
          sessionId,
          filename: fileToSend.name,
          contentType: fileToSend.type || 'application/octet-stream',
        });
        await fetch(data.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': fileToSend.type || 'application/octet-stream' },
          body: fileToSend,
        });
        attachmentMeta = { s3Key: data.s3Key, contentType: data.contentType, filename: data.filename };
      }

      // Otimista: mostra a mensagem local imediatamente
      setMessages(prev => [...prev, {
        from: 'me',
        text: text || (attachmentMeta ? `[${attachmentMeta.contentType?.startsWith('image/') ? t('mediaImage', { defaultValue: 'Imagem' }) : t('mediaFile', { defaultValue: 'Arquivo' })}: ${attachmentMeta.filename}]` : ''),
        attachments: attachmentMeta ? [attachmentMeta] : undefined,
        time: fmtTime(),
      }]);

      // 2. Envia a mensagem com metadados do anexo
      const payload = { message: text, ...(attachmentMeta || {}) };
      if (active.type === 'agent') {
        await api.post(`/internal-chat/${tenantId}/message`, { ...payload, recipientId: active.id, recipientType: 'agent' });
      } else if (active.type === 'human') {
        await api.post(`/internal-chat/${tenantId}/message`, { ...payload, recipientId: active.id, recipientType: 'human' });
      } else if (active.type === 'group') {
        await api.post(`/internal-chat/${tenantId}/message`, { ...payload, recipientType: 'wa_group', teamGroupId: active.id });
      }
    } catch (e) {
      setMessages(prev => [...prev, { from: 'error', text: e.response?.data?.message || e.message, time: fmtTime() }]);
    } finally { setSending(false); }
  };

  // Renderiza bolhas de mídia (imagem inline / link de download) para uma mensagem.
  const renderAttachments = (msg) => {
    if (!msg.attachments?.length) return null;
    return msg.attachments.map((att, i) => {
      const url = mediaUrls[att.s3Key];
      if (!url) loadMediaUrl(att.s3Key);
      if (att.contentType?.startsWith('image/')) {
        return url
          ? <MediaImg key={i} src={url} alt={att.filename || ''} onClick={() => window.open(url, '_blank')} />
          : <MediaPlaceholder key={i}><ImageIcon size={14} /> {t('loadingMedia', { ns: 'callcenter', defaultValue: 'Carregando imagem...' })}</MediaPlaceholder>;
      }
      if (att.contentType?.startsWith('audio/')) {
        return url
          ? <audio key={i} controls src={url} style={{ maxWidth: '100%', marginTop: 4 }} />
          : <MediaPlaceholder key={i}><FileText size={14} /> {att.filename}</MediaPlaceholder>;
      }
      return url
        ? <MediaLink key={i} href={url} target="_blank" rel="noreferrer"><FileText size={12} /> {att.filename || t('attachment', { ns: 'callcenter', defaultValue: 'Anexo' })}</MediaLink>
        : <MediaPlaceholder key={i}><FileText size={14} /> {att.filename}</MediaPlaceholder>;
    });
  };

  const q = search.toLowerCase();
  const filteredAgents = agents.filter(a => a.name.toLowerCase().includes(q));
  const filteredMembers = members.filter(m => (m.name || m.email || '').toLowerCase().includes(q));
  const filteredGroups = groups.filter(g => g.name.toLowerCase().includes(q));

  return (
    <Overlay onClick={onClose}>
      <Drawer $expanded={expanded} onClick={e => e.stopPropagation()}>
        <DrawerHeader>
          {active ? (
            <>
              <BackBtn aria-label={t('common:back')} onClick={() => setActive(null)}>←</BackBtn>
              <DrawerTitle>{active.name}</DrawerTitle>
            </>
          ) : (
            <DrawerTitle>{t('internalChatBtn', { defaultValue: 'Nova Conversa' })}</DrawerTitle>
          )}
          <IconBtn
            aria-label={expanded ? t('collapsePanel', { defaultValue: 'Recolher' }) : t('expandPanel', { defaultValue: 'Expandir' })}
            title={expanded ? t('collapsePanel', { defaultValue: 'Recolher' }) : t('expandPanel', { defaultValue: 'Expandir' })}
            onClick={() => setExpanded(e => !e)}
          >
            {expanded ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </IconBtn>
          <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
        </DrawerHeader>

        {!active ? (
          <>
            <SearchWrap>
              <Search size={14} color={theme.colors.textMuted} />
              <SearchInput
                autoFocus
                placeholder={`${t('common:search')}...`}
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </SearchWrap>

            <ContactList>
              {loading && <LoadingMsg>{t('common:loading')}</LoadingMsg>}

              {filteredAgents.length > 0 && (
                <>
                  <SectionLabel>{t('aiAgents', { defaultValue: 'Agentes IA' })}</SectionLabel>
                  {filteredAgents.map(a => (
                    <ContactRow key={a.id} onClick={() => setActive(a)}>
                      <ContactIcon $bot><Bot size={14} /></ContactIcon>
                      <span>{a.name}</span>
                    </ContactRow>
                  ))}
                </>
              )}

              {filteredMembers.length > 0 && (
                <>
                  <SectionLabel>{t('members', { defaultValue: 'Membros' })}</SectionLabel>
                  {filteredMembers.map(m => (
                    <ContactRow key={m.memberId || m.email} onClick={() => setActive({ type: 'human', id: m.memberId || m.email, name: m.name || m.email })} style={unreadFrom === (m.memberId || m.email) ? { fontWeight: 'bold', background: theme.colors?.primary10 || '#e8f4fd' } : {}}>
                      <ContactIcon><User size={14} /></ContactIcon>
                      <span>{m.name || m.email}{unreadFrom === (m.memberId || m.email) ? ' 💬' : ''}</span>
                    </ContactRow>
                  ))}
                </>
              )}

              {filteredGroups.length > 0 && (
                <>
                  <SectionLabel>{t('groups', { defaultValue: 'Grupos' })}</SectionLabel>
                  {filteredGroups.map(g => (
                    <ContactRow key={g.id} onClick={() => setActive({ type: 'group', id: g.id, name: g.name })}>
                      <ContactIcon $group><Users size={14} /></ContactIcon>
                      <span>{g.name}</span>
                    </ContactRow>
                  ))}
                </>
              )}

              {!loading && filteredAgents.length === 0 && filteredMembers.length === 0 && filteredGroups.length === 0 && (
                <LoadingMsg>{t('noResults', { defaultValue: 'Nenhum resultado' })}</LoadingMsg>
              )}
            </ContactList>
          </>
        ) : (
          <>
            <ChatMessages>
              {messages.length === 0 && (
                <PlaceholderMsg>
                  {active.type === 'agent' ? t('askAgentPlaceholder', { defaultValue: 'Faça uma pergunta ao agente...' }) : t('conversationWith', { name: active.name, defaultValue: 'Conversa com {{name}}' })}
                </PlaceholderMsg>
              )}
              {messages.map((msg, i) => (
                <ChatMsg key={i} $from={msg.from}>
                  {msg.from === 'other' && msg.sender && <SenderName>{msg.sender}</SenderName>}
                  <ChatBubble $from={msg.from}>
                    {msg.attachments?.length > 0 && <MediaWrap>{renderAttachments(msg)}</MediaWrap>}
                    {msg.text && <span>{msg.text}</span>}
                  </ChatBubble>
                  <ChatTime>{msg.time}</ChatTime>
                </ChatMsg>
              ))}
              <div ref={endRef} />
            </ChatMessages>

            {attachedFile && (
              <AttachPreview>
                {attachedFile.type?.startsWith('image/') ? <ImageIcon size={14} /> : <FileText size={14} />}
                <AttachName title={attachedFile.name}>{attachedFile.name}</AttachName>
                <AttachRemove aria-label={t('removeAttachment', { defaultValue: 'Remover anexo' })} onClick={removeAttachedFile}><X size={12} /></AttachRemove>
              </AttachPreview>
            )}
            <ChatInputRow>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              {active.type !== 'group' && (
                <AttachBtn
                  aria-label={t('attachFile', { ns: 'callcenter', defaultValue: 'Anexar arquivo' })}
                  title={t('attachFile', { ns: 'callcenter', defaultValue: 'Anexar arquivo' })}
                  onClick={() => fileInputRef.current?.click()}
                  disabled={sending}
                >
                  <Paperclip size={16} />
                </AttachBtn>
              )}
              <ChatInput
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && (e.preventDefault(), send())}
                placeholder={t('typeMessage')}
                disabled={sending}
              />
              <SendBtn aria-label={t('common:send')} onClick={send} disabled={sending || (!input.trim() && !attachedFile)}>
                {sending ? <Spinner size="14px" /> : <Send size={14} />}
              </SendBtn>
            </ChatInputRow>
          </>
        )}
      </Drawer>
    </Overlay>
  );
};

function fmtTime(iso) {
  return new Date(iso || Date.now()).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}


const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  z-index: 900;
  display: flex;
  justify-content: flex-end;
`;

const Drawer = styled.div`
  width: ${p => (p.$expanded ? 'min(760px, 90vw)' : '340px')};
  height: 100dvh;
  background: ${theme.colors.white};
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 24px rgb(0 0 0 / 0.12);
  animation: slideIn 200ms ease;
  transition: width 220ms ease;
  @keyframes slideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
  @media (max-width: 767px) {
    width: 100%;
    height: ${p => (p.$expanded ? '100dvh' : '75dvh')};
    border-radius: ${p => (p.$expanded ? '0' : `${theme.radii['2xl']} ${theme.radii['2xl']} 0 0`)};
    box-shadow: 0 -4px 24px rgb(0 0 0 / 0.15);
    transition: height 220ms ease;
    @keyframes slideIn { from { transform: translateY(100%); } to { transform: translateY(0); } }
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.gray50};
  flex-shrink: 0;
`;

const DrawerTitle = styled.div`
  flex: 1;
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 18px;
  color: ${theme.colors.textSecondary};
  padding: 0 ${theme.spacing[1]};
  &:hover { color: ${theme.colors.textPrimary}; }
`;

const IconBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  padding: ${theme.spacing[1]};
  border-radius: ${theme.radii.md};
  &:hover { color: ${theme.colors.textPrimary}; background: ${theme.colors.gray100}; }
  @media (max-width: 767px) { display: none; }
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  padding: ${theme.spacing[1]};
  border-radius: ${theme.radii.md};
  &:hover { color: ${theme.colors.textPrimary}; background: ${theme.colors.gray100}; }
`;

const SearchWrap = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  background: transparent;
  &::placeholder { color: ${theme.colors.textMuted}; }
`;

const ContactList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing[2]} 0;
`;

const SectionLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: ${theme.spacing[2]} ${theme.spacing[4]} ${theme.spacing[1]};
`;

const ContactRow = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textPrimary};
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.gray50}; }
`;

const ContactIcon = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${p => p.$bot ? theme.colors.primaryLight : p.$group ? theme.colors.successLight : theme.colors.gray100};
  color: ${p => p.$bot ? theme.colors.primary : p.$group ? theme.colors.success : theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const LoadingMsg = styled.div`
  padding: ${theme.spacing[6]} ${theme.spacing[4]};
  text-align: center;
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
`;

// Chat inline
const ChatMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing[3]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  background: ${theme.colors.chatBg};
`;

const PlaceholderMsg = styled.div`
  text-align: center;
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  padding: ${theme.spacing[4]} 0;
`;

const ChatMsg = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${p => p.$from === 'me' ? 'flex-end' : 'flex-start'};
`;

const SenderName = styled.div`
  font-size: 10px;
  color: ${theme.colors.textMuted};
  margin-bottom: 2px;
  padding: 0 4px;
`;

const ChatBubble = styled.div`
  max-width: 85%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${p => p.$from === 'me' ? '12px 12px 2px 12px' : '12px 12px 12px 2px'};
  background: ${p => p.$from === 'me' ? theme.colors.chatBubbleAgent : p.$from === 'error' ? '#fee2e2' : theme.colors.white};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.sm};
  line-height: 1.5;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.08);
`;

const ChatTime = styled.div`
  font-size: 10px;
  color: ${theme.colors.textMuted};
  margin-top: 2px;
  padding: 0 4px;
`;

const ChatInputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]};
  background: ${theme.colors.white};
  border-top: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const ChatInput = styled.textarea`
  flex: 1;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  font-size: ${theme.typography.sm};
  font-family: inherit;
  resize: none;
  height: 38px;
  line-height: 1.4;
  outline: none;
  background: ${theme.colors.gray50};
  &:focus { border-color: ${theme.colors.primary}; background: white; }
`;

const SendBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: ${theme.transitions.fast};
  &:hover:not(:disabled) { background: ${theme.colors.primaryHover}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const AttachBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${theme.colors.gray100};
  color: ${theme.colors.textSecondary};
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: ${theme.transitions.fast};
  &:hover:not(:disabled) { background: ${theme.colors.gray200}; color: ${theme.colors.textPrimary}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const AttachPreview = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  margin: 0 ${theme.spacing[3]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.gray50};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textSecondary};
`;

const AttachName = styled.span`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AttachRemove = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  padding: 2px;
  border-radius: ${theme.radii.sm};
  &:hover { color: ${theme.colors.error}; background: ${theme.colors.gray100}; }
`;

const MediaWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 4px;
`;

const MediaImg = styled.img`
  max-width: 220px;
  max-height: 220px;
  border-radius: ${theme.radii.md};
  cursor: pointer;
  object-fit: cover;
`;

const MediaLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${theme.colors.primary};
  font-size: ${theme.typography.sm};
  text-decoration: none;
  padding: ${theme.spacing[1]} 0;
  &:hover { text-decoration: underline; }
`;

const MediaPlaceholder = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.xs};
  font-style: italic;
`;

export default NewChatDrawer;
